import { CommonModule } from "@angular/common";
import { Location } from "@angular/common";
import { Component, OnDestroy, ViewChild, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AlertController, IonContent, ModalController, NavParams } from "@ionic/angular";
import { IonicModule } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { BehaviorSubject, Observable, Subject, combineLatest, of } from "rxjs";
import { distinctUntilChanged, takeUntil } from "rxjs/operators";

//Services
import { UserService } from "@providers/UserService";
import { BufferFacade } from "@providers/facades/buffer.facade";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";
import { SportBookService, SportNavigationService, SportService, TicketService } from "@sports-services/index";

import { NewTicketFacade } from "@sports-features/ticket/stores";
// Stores
import { SportsbookFacade } from "@sports-stores/sportsbook/sportsbook.facade";
import { SportsbookState } from "@sports-stores/sportsbook/sportsbook.reducers";
import * as sportsbookSelector from "@sports-stores/sportsbook/sportsbook.selectors";

//Pipes
import { SportsExtractDatePipe } from "@sports-pipes/index";

// Routes
import { SPORTS_PATHS } from "@shared-constants/routes";

// Components
import {
  SbCounterComponent,
  SbIconBarComponent,
  SbOverviewScoreRegularComponent,
  SbOverviewScoreSetsComponent,
  SbTabsCategoriesComponent,
  SportsBetButtonComponent,
  SportsGridComponent,
  SportsGridContentTeamsComponent,
  SportsGroupComponent,
  SportsLiveNavbarComponent,
} from "@sports-components/index";

import { CodereFooterComponent } from "../../../../shared/components/mso/footer/codere-footer/codere-footer";

// Models
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { SportNavItem } from "@sports-models/index";
import { SportsBasePage, TrackingInfo } from "@sports-pages/index";

import { TicketItemTypesEnum } from "@sports-pages/ticket/utils/ticket.constants";
//Others
import { existsIn } from "@sports-services/sports.navigation.service/sports.navigation.utils";
import { SportsConstants } from "@sports-utils/index";
import { SCOREBOARD_TYPE } from "@sports-utils/sports.constants";

@Component({
  standalone: true,
  imports: [
    CodereFooterComponent,
    CommonModule,
    IonicModule,
    SbIconBarComponent,
    SportsLiveNavbarComponent,
    SbOverviewScoreRegularComponent,
    SbOverviewScoreSetsComponent,
    SbTabsCategoriesComponent,
    SbCounterComponent,
    SportsBetButtonComponent,
    SportsGridComponent,
    SportsGroupComponent,
    SportsGridContentTeamsComponent,
  ],
  selector: "codere-page-live",
  templateUrl: "./live.page.html",
  styleUrls: ["./live.page.scss"],
})
export class DirectosPage extends SportsBasePage implements OnDestroy {
  @ViewChild("content") content: IonContent;
  globals!: GlobalVarsModel;
  sportsNav$: Observable<SportNavItem[]>;
  categories$: Observable<any[]>;
  selectedSportsNav$: Observable<any>;
  selectedSportsNav;
  selectedCategory$: BehaviorSubject<any> = new BehaviorSubject("");
  selectedCategory;
  liveData$: Observable<any>;
  sportName: string;

  extractDate = inject(SportsExtractDatePipe);
  route = inject(ActivatedRoute);
  router = inject(Router);
  deviceService = inject(DeviceService);
  newTicketFacade = inject(NewTicketFacade);
  sportBookService = inject(SportBookService);
  sportsbookFacade = inject(SportsbookFacade);
  store = inject(Store<SportsbookState>);
  sportNavigationService = inject(SportNavigationService);
  newBaseService = inject(NewBaseService);

  private ngUnsubscribe$ = new Subject<boolean>();
  liveSport$: Subject<any> = new Subject();
  filteredLiveSport$: Observable<any>;

  breadcrumbs = ["Ahora en Directo"];
  SoccerId = "103843409";

  isProcessing = false;

  firstStepRestoreScrollAndSelectionDone = false;
  secondStepRestoreScrollAndSelectionTrigger = false;
  sportsNavigationId = undefined;
  stateToSave = {
    selectedSportsNav: null,
    selectedCategory: null,
    scrollPosition: null,
  };
  stateToRestore = undefined;

  isDesktop: boolean;
  isMobile: boolean;

  SCOREBOARD_TYPE = SCOREBOARD_TYPE;

  constructor(
    public navParams: NavParams,
    public sportService: SportService,
    public ticketService: TicketService,
    public userService: UserService,
    public alertCtrl: AlertController,
    public modalCtrl: ModalController,
    public bufferFacade: BufferFacade,
    public location: Location,
  ) {
    super();
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();

    this.initializePage();
  }

  initializePage() {
    this.newBaseService.getVars.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((data: GlobalVarsModel) => {
      this.globals = data;
    });

    this.selectedSportsNav = null;
    this.sportsbookFacade.initLive();

    const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
    this.sportsNavigationId = currentUrl.state.sportsNavigationId;

    this.liveData$ = this.store.select(sportsbookSelector.getLiveDataState);
    this.liveData$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((data) => {
      this.isProcessing = !data.sport?.Leagues?.length;
      this.sportsNav$ = of(data.sportsNav);
      this.categories$ = of(data.categories);
      this.liveSport$.next(data.sport);
      this.selectedSportsNav = data.selectedSportsNav;

      if (this.secondStepRestoreScrollAndSelectionTrigger && data.categories.length) {
        this.restoreScrollAndSelectionsSecondStep(data.categories);
      }
      const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
      if (currentUrl?.popstate && data?.sportsNav?.length && !this.firstStepRestoreScrollAndSelectionDone) {
        this.sportsNavigationId = currentUrl.state.sportsNavigationId;
        this.stateToRestore = this.sportNavigationService.getRestoreStateFromHistory(this.sportsNavigationId);
        if (this.stateToRestore) {
          this.stateToSave = this.stateToRestore;
          this.restoreScrollAndSelectionsFirstStep(data.sportsNav);
        }
      }
      this.selectedCategory$.next(data.selectedCategory);
    });

    this.filteredLiveSport$ = combineLatest(
      [this.selectedCategory$.pipe(distinctUntilChanged()), this.liveSport$],
      (category, sport) => {
        let filteredLiveSport = sport;
        if (!!category && category.CategoryId !== SportsConstants.principalesCat) {
          filteredLiveSport = {
            ...sport,
            Leagues: sport.Leagues?.filter((league) => league.CountryCode === category.term).map((league) => ({
              ...league,
              filterCategoryId: category.CategoryId,
            })),
          };
        } else {
          filteredLiveSport = {
            ...sport,
            Leagues: sport.Leagues?.map((league) => ({
              ...league,
              filterCategoryId: SportsConstants.principalesCat,
            })),
          };
        }
        return filteredLiveSport;
      },
    );
    this.globals.hideBetFooterBar = false;
  }

  restoreScrollAndSelectionsFirstStep(sportsNav) {
    if (
      this.stateToRestore.selectedSportsNav &&
      existsIn(this.stateToRestore.selectedSportsNav, sportsNav, "NodeId")
    ) {
      this.setSelectedSportsNav(this.stateToRestore.selectedSportsNav, undefined);
    }
    this.secondStepRestoreScrollAndSelectionTrigger = true;
    this.firstStepRestoreScrollAndSelectionDone = true;
  }

  restoreScrollAndSelectionsSecondStep(categories) {
    if (
      this.stateToRestore.selectedCategory &&
      existsIn(this.stateToRestore.selectedCategory, categories, "CategoryId")
    ) {
      this.sportsbookFacade.setLiveSelectedCategory(this.stateToRestore.selectedCategory);
    }
    setTimeout(() => {
      this.content.scrollToPoint(undefined, this.stateToRestore.scrollPosition || 0, 259);
    }, 150);
    this.secondStepRestoreScrollAndSelectionTrigger = false;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next(true);
    this.ngUnsubscribe$.complete();

    this.sportNavigationService.injectLastStateToHistory(this.sportsNavigationId, this.stateToSave);
  }

  setSelectedSportsNav(sport, index?: number) {
    this.stateToSave = {
      ...this.stateToSave,
      selectedSportsNav: sport,
    };
    this.sportsbookFacade.setSelectedSportsNav(sport);
    this.selectedSportsNav = sport;
    this.selectedCategory$.next(null);
    this.isProcessing = true;
    this.trackingService.trackEvent([
      "ChangeSportInDirectosPage",
      sport.SportId,
      sport.icon,
      index?.toString() || 0,
      "event",
    ]);
    this.content?.scrollToTop(250);
  }

  setCurrentFilter(filter) {
    this.sportsbookFacade.setLiveSelectedCategory(filter);
    this.stateToSave = {
      ...this.stateToSave,
      selectedCategory: filter,
    };
    this.content.scrollToTop(250);
  }

  public bet(game, event, marketName: string) {
    const data = {
      ...game,
      EventDate: this.extractDate.transform(event.StartDate),
      EventName: event.Name,
      GameName: marketName,
    };

    this.newTicketFacade.checkSelection(data, data.NodeId, TicketItemTypesEnum.LINE_ITEM);
  }

  navigate(route: string, params?, gameType?) {
    if (gameType) {
      return;
    }
    let componentUrl;
    switch (route) {
      case "home":
        componentUrl = "";
        break;
      case "live-market":
        componentUrl = SPORTS_PATHS.NowLiveDetailPage;
        break;
    }
    this.router.navigate([componentUrl], {
      state: { params },
    });
  }

  trackLeague(index, item) {
    return item.NodeId + item.filterCategoryId;
  }

  getTrackingInfo(): TrackingInfo {
    return {
      uri: "/live",
      description: "Directos page",
      additionalData: null,
    };
  }

  onScroll(ev) {
    this.stateToSave = {
      ...this.stateToSave,
      scrollPosition: ev.detail.scrollTop,
    };
  }
}
